// Conversation Manager for Avatar
export class ConversationManager {
  private isProcessing: boolean = false;
  private currentSession: string = '';
  private conversationHistory: Array<{
    timestamp: string;
    userInput: string;
    aiResponse: string;
    emotion: string;
    status: 'processing' | 'completed' | 'error';
  }> = [];

  constructor() {
    this.currentSession = `session-${Date.now()}`;
  }

  async processUserMessage(userInput: string): Promise<string> {
    if (this.isProcessing) {
      console.log('Already processing, ignoring new input');
      return 'I am still processing your previous request. Please wait a moment.';
    }

    this.isProcessing = true;
    
    try {
      console.log('🔄 Processing user message:', userInput);
      
      // Analyze emotion from user input
      const emotion = this.analyzeEmotion(userInput);
      
      // Add to conversation history
      this.conversationHistory.push({
        timestamp: new Date().toLocaleTimeString(),
        userInput,
        aiResponse: 'Processing...',
        emotion: emotion,
        status: 'processing'
      });

      // Determine response type with emotion
      const response = await this.generateResponse(userInput, emotion);
      
      // Update conversation history
      const lastEntry = this.conversationHistory[this.conversationHistory.length - 1];
      lastEntry.aiResponse = response;
      lastEntry.status = 'completed';

      console.log('✅ Response generated:', response);
      return response;

    } catch (error) {
      console.error('❌ Error processing message:', error);
      
      // Update conversation history with error
      const lastEntry = this.conversationHistory[this.conversationHistory.length - 1];
      lastEntry.aiResponse = 'I apologize, but I encountered an error processing your request.';
      lastEntry.status = 'error';
      
      return 'I apologize, but I encountered an error processing your request.';
    } finally {
      this.isProcessing = false;
    }
  }

  private async generateResponse(userInput: string, emotion: string): Promise<string> {
    const lowerInput = userInput.toLowerCase();
    
    // Check if it's a greeting
    if (this.isGreeting(lowerInput)) {
      return this.generateGreetingResponse(emotion);
    }
    
    // Check if it's a portfolio question
    if (this.isPortfolioQuestion(lowerInput)) {
      return await this.getPortfolioResponse(userInput, emotion);
    }
    
    // Check if it's a market question
    if (this.isMarketQuestion(lowerInput)) {
      return await this.getMarketResponse(userInput, emotion);
    }
    
    // Check if it's a trading question
    if (this.isTradingQuestion(lowerInput)) {
      return await this.getTradingResponse(userInput, emotion);
    }
    
    // Check if it's a help question
    if (this.isHelpQuestion(lowerInput)) {
      return this.generateHelpResponse(emotion);
    }
    
    // Default response
    return this.generateDefaultResponse(userInput, emotion);
  }

  private analyzeEmotion(input: string): string {
    const lowerInput = input.toLowerCase();
    
    // Analyze emotional keywords
    const excitedKeywords = ['great', 'amazing', 'wow', 'excellent', 'fantastic', 'incredible'];
    const concernedKeywords = ['worried', 'concerned', 'scared', 'nervous', 'anxious', 'fear'];
    const calmKeywords = ['relaxed', 'calm', 'peaceful', 'steady', 'stable'];
    const confidentKeywords = ['confident', 'sure', 'certain', 'definitely', 'absolutely'];
    
    if (excitedKeywords.some(keyword => lowerInput.includes(keyword))) {
      return 'excited';
    } else if (concernedKeywords.some(keyword => lowerInput.includes(keyword))) {
      return 'concerned';
    } else if (calmKeywords.some(keyword => lowerInput.includes(keyword))) {
      return 'calm';
    } else if (confidentKeywords.some(keyword => lowerInput.includes(keyword))) {
      return 'confident';
    }
    
    return 'friendly'; // Default emotion
  }

  private isGreeting(input: string): boolean {
    const greetings = ['hello', 'hi', 'hey', 'good morning', 'good afternoon', 'good evening'];
    return greetings.some(greeting => input.includes(greeting));
  }

  private isPortfolioQuestion(input: string): boolean {
    const portfolioKeywords = ['portfolio', 'balance', 'value', 'holdings', 'investments', 'account'];
    return portfolioKeywords.some(keyword => input.includes(keyword));
  }

  private isMarketQuestion(input: string): boolean {
    const marketKeywords = ['market', 'stock', 'price', 'trading', 's&p', 'dow', 'nasdaq'];
    return marketKeywords.some(keyword => input.includes(keyword));
  }

  private isTradingQuestion(input: string): boolean {
    const tradingKeywords = ['trade', 'buy', 'sell', 'order', 'position', 'strategy'];
    return tradingKeywords.some(keyword => input.includes(keyword));
  }

  private isHelpQuestion(input: string): boolean {
    const helpKeywords = ['help', 'how', 'what can you do', 'assist', 'support'];
    return helpKeywords.some(keyword => input.includes(keyword));
  }

  private generateGreetingResponse(emotion: string): string {
    const greetings = {
      excited: [
        "Wow, hello there! I'm your RICHGURU AI assistant and I'm excited to help you with your trading and investment needs today!",
        "Hey there! I'm thrilled to assist you with portfolio analysis, market insights, and trading strategies. What would you like to explore?"
      ],
      calm: [
        "Hello. I'm your RICHGURU AI assistant. I'm here to help you with your trading and investment needs in a calm, measured way.",
        "Greetings. I'm your AI trading assistant, ready to help you with portfolio management and market analysis."
      ],
      concerned: [
        "Hello, I'm your RICHGURU AI assistant. I understand you might have concerns, and I'm here to help you with your trading and investment needs.",
        "Hi there. I'm here to help you with portfolio analysis and market insights. Let me know what's on your mind."
      ],
      confident: [
        "Hello! I'm your RICHGURU AI assistant, and I'm confident I can help you with your trading and investment needs today.",
        "Greetings! I'm your AI trading assistant, and I'm certain I can assist you with portfolio analysis and market insights."
      ],
      friendly: [
        "Hello! I'm your RICHGURU AI assistant. How can I help you with your trading and investment needs today?",
        "Hi there! I'm here to help you with portfolio analysis, market insights, and trading strategies. What would you like to know?",
        "Greetings! I'm your AI trading assistant. I can help you with portfolio management, market analysis, and trading decisions. What's on your mind?"
      ]
    };
    
    const emotionGreetings = greetings[emotion as keyof typeof greetings] || greetings.friendly;
    return emotionGreetings[Math.floor(Math.random() * emotionGreetings.length)];
  }

  private async getPortfolioResponse(userInput: string, _emotion?: string): Promise<string> {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || 'http://localhost:8050'}/api/v1/avatar/avatar/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: userInput,
          session_id: this.currentSession,
          user_id: 'demo-user'
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.response || 'I understand you\'re asking about your portfolio. Let me provide you with some helpful information.';
      } else {
        return 'I understand you\'re asking about your portfolio. Let me provide you with some helpful information about your investments and market conditions.';
      }
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
      return 'I understand you\'re asking about your portfolio. Let me provide you with some helpful information about your investments and market conditions.';
    }
  }

  private async getMarketResponse(_userInput: string, _emotion?: string): Promise<string> {
    return 'The market is showing mixed signals today. The S&P 500 is up 0.8% while tech stocks are slightly down. Would you like me to analyze any specific stocks or market sectors?';
  }

  private async getTradingResponse(_userInput: string, _emotion?: string): Promise<string> {
    return 'I can help you with trading decisions. What specific trade are you considering? I\'ll analyze the current market conditions and provide recommendations.';
  }

  private generateHelpResponse(_emotion?: string): string {
    return 'I\'m here to help with your trading and investment needs. You can ask me about your portfolio, market analysis, trading strategies, or any financial questions you have. What would you like to know?';
  }

  private generateDefaultResponse(userInput: string, _emotion?: string): string {
    return `I heard you say "${userInput}". I'm here to help with your trading and investment needs. You can ask me about your portfolio, market analysis, trading strategies, or any financial questions you have.`;
  }

  getConversationHistory() {
    return this.conversationHistory;
  }

  isCurrentlyProcessing(): boolean {
    return this.isProcessing;
  }
} 